import type { Property, ThirdPartyListing } from "@shared/schema";

export type PropertyListRow = Property & {
  bedroomCount?: number;
  hasRoomData?: boolean;
  setupProgress?: { completed: number; total: number; percent: number };
};
export type GroupBy = "none" | "state" | "propertyGroup";
export type SortCol = "name" | "status" | "state" | "rooms" | "listings" | "propertyGroup" | "padsplitId" | "setupProgress";
export type SortDir = "asc" | "desc";
export const SORT_LABELS: Record<SortCol, string> = {
  name: "Name", status: "Status", state: "State",
  rooms: "Rooms", listings: "Listings", propertyGroup: "Group", padsplitId: "PadSplit ID", setupProgress: "Setup",
};
export const displayRoomCount = (p: PropertyListRow): number =>
  p.hasRoomData ? (p.bedroomCount ?? 0) : (p.roomCount ?? 0);

export function readListPreferences(storage?: Storage) {
  const col = storage?.getItem("properties:sortCol");
  const validCol = col != null && Object.hasOwn(SORT_LABELS, col);
  const group = storage?.getItem("properties:groupBy");
  return {
    sortCol: validCol ? col as SortCol : "name" as SortCol,
    sortDir: validCol && storage?.getItem("properties:sortDir") === "desc" ? "desc" as SortDir : "asc" as SortDir,
    groupBy: group === "state" || group === "propertyGroup" ? group : "none" as GroupBy,
  };
}

/** All active platforms count, including custom and no-longer-configured platforms. */
export function activeListingCounts(listings: ThirdPartyListing[]) {
  const result = new Map<number, Map<string, number>>();
  for (const listing of listings) {
    if (listing.status !== "active" || listing.propertyId == null) continue;
    const counts = result.get(listing.propertyId) ?? new Map<string, number>();
    counts.set(listing.platform, (counts.get(listing.platform) ?? 0) + 1);
    result.set(listing.propertyId, counts);
  }
  return result;
}

export function totalActiveListings(counts?: Map<string, number>) {
  return counts ? [...counts.values()].reduce((sum, count) => sum + count, 0) : 0;
}

const STATUS_ORDER: Record<string, number> = {
  underwriting: 1, acquisition: 2, renovations: 3, launch: 4, active: 5,
  archive: 0, sold: 0, disposed: 0,
};
export function compareProperties(
  a: PropertyListRow, b: PropertyListRow, col: SortCol, dir: SortDir,
  listings: Map<number, Map<string, number>>,
) {
  const name = a.name.localeCompare(b.name);
  const flip = dir === "asc" ? 1 : -1;
  const compareOptional = (av: string | number | undefined, bv: string | number | undefined) => {
    if (av === undefined && bv === undefined) return name;
    if (av === undefined) return 1;
    if (bv === undefined) return -1;
    const diff = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
    return diff ? flip * diff : name;
  };
  switch (col) {
    case "name": return flip * name;
    case "state": return compareOptional(a.state?.trim() || undefined, b.state?.trim() || undefined);
    case "propertyGroup": return compareOptional(a.propertyGroup?.trim() || undefined, b.propertyGroup?.trim() || undefined);
    case "padsplitId": return compareOptional(a.padsplitPropertyId || undefined, b.padsplitPropertyId || undefined);
    case "rooms": return compareOptional(displayRoomCount(a), displayRoomCount(b));
    case "listings": return compareOptional(totalActiveListings(listings.get(a.id)), totalActiveListings(listings.get(b.id)));
    case "setupProgress": {
      const percent = (property: PropertyListRow) => {
        const value = property.setupProgress?.percent;
        return typeof value === "number" && Number.isFinite(value) ? value : undefined;
      };
      return compareOptional(percent(a), percent(b));
    }
    case "status": {
      const diff = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
      return diff ? flip * diff : (STATUS_ORDER[a.status] === undefined && STATUS_ORDER[b.status] === undefined
        ? flip * a.status.localeCompare(b.status) || name : name);
    }
  }
}

export function groupProperties(rows: PropertyListRow[], groupBy: GroupBy) {
  if (groupBy === "none") return [];
  const groups = new Map<string, PropertyListRow[]>();
  for (const p of rows) {
    const key = p[groupBy]?.trim() || "";
    const items = groups.get(key) ?? [];
    items.push(p);
    groups.set(key, items);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => !a ? 1 : !b ? -1 : a.localeCompare(b))
    .map(([key, items]) => ({ key, label: key || (groupBy === "state" ? "No state set" : "Ungrouped"), items }));
}
